// The campaign-type prefix every Amplemarket sequence name carries: "[Fundraise] Series A".
// Cartesia names sequences this way so they roll up into Salesforce customer journeys.
//
// The server keeps its own copy in `sequence_builder/campaign_types.py` and validates against
// it; `tests/test_sequence_builder.py` fails if the two lists differ. Edit both together.

export const CAMPAIGN_TYPE_GROUPS: { group: string; types: string[] }[] = [
  { group: 'Intent Signal', types: ['LinkedIn - Cartesia', 'LinkedIn - Competitor', 'G2 - Cartesia', 'G2 - Competitor',
    'Fundraise', 'Job Change'] },
  { group: 'Product', types: ['Interesting Signup', 'Concurrency Limit'] },
  { group: 'Cold Outbound', types: ['Lookalike', 'Industry', 'Location', 'Language', 'GTM Strategist'] },
  { group: 'Website Visitor', types: ['Website Visitor'] },
  { group: 'Event', types: ['Trade Show', 'Webinar', 'On Demand Webinar', 'Cartesia Hosted'] },
];

export const CAMPAIGN_TYPES: string[] = CAMPAIGN_TYPE_GROUPS.flatMap(entry => entry.types);

export const DEFAULT_CAMPAIGN_TYPE = 'GTM Strategist';

// A leading "[anything]" and the space after it.
const BRACKET_PREFIX_PATTERN = /^\s*\[([^\]]*)\]\s*/;

export interface SplitName {
  // The bracketed text at the start, as typed; null when the name has no prefix.
  prefix: string | null;
  rest: string;
}

export function splitName(name: string): SplitName {
  const match = BRACKET_PREFIX_PATTERN.exec(name);
  if (!match) return { prefix: null, rest: name };
  return { prefix: (match[1] ?? '').trim(), rest: name.slice(match[0].length) };
}

export function isCampaignType(value: string | null): value is string {
  return value !== null && CAMPAIGN_TYPES.includes(value);
}

// The name the sequence is created with. A known prefix already typed is replaced, never doubled.
export function prefixedName(campaignType: string, typedName: string): string {
  const split = splitName(typedName);
  const rest = isCampaignType(split.prefix) ? split.rest : typedName;
  return `[${campaignType}] ${rest.trim()}`.trim();
}

// A typed bracket prefix the server would refuse; null when there is none or it is on the list.
export function unknownPrefix(typedName: string): string | null {
  const prefix = splitName(typedName).prefix;
  return prefix !== null && !isCampaignType(prefix) ? prefix : null;
}
